// ---------------------------------------------------------------------------
// Standalone, deep-linkable product page.
//
// Reuses the same ProductFeature component as the homepage, always active, so
// the two surfaces cannot drift apart visually.
// ---------------------------------------------------------------------------

import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { api } from '../lib/apiClient.js';
import { useSite } from '../context/SiteProvider.jsx';
import { usePageMeta } from '../hooks/usePageMeta.js';
import { ProductFeature } from '../components/product/ProductFeature.jsx';
import { CatalogCard } from '../components/product/CatalogCard.jsx';
import { ProductSkeleton } from '../components/common/Skeletons.jsx';
import { Reveal } from '../components/common/Reveal.jsx';
import NotFound from './NotFound.jsx';

export default function Product() {
  const { slug } = useParams();
  const { t, pick } = useSite();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['product', slug],
    queryFn: () => api.get(`/products/${slug}`),
    retry: false,
  });

  const { data: all } = useQuery({
    queryKey: ['products', 'summary'],
    queryFn: () => api.get('/products'),
    staleTime: 5 * 60 * 1000,
  });

  usePageMeta(
    data?.product ? pick(data.product.name) : null,
    data?.product ? pick(data.product.intro) : undefined,
  );

  if (isLoading) {
    return (
      <main id="main">
        <ProductSkeleton />
      </main>
    );
  }

  if (isError || !data?.product) return <NotFound />;

  const product = data.product;
  const related = (all?.products ?? [])
    .filter((entry) => entry.id !== product.id && entry.category?.slug === product.category?.slug)
    .slice(0, 3);

  return (
    <main id="main">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/">{t('navHome')}</Link>
        <span aria-hidden="true">/</span>
        <Link to="/shop">{t('navShop')}</Link>
        <span aria-hidden="true">/</span>
        <b>{pick(product.name)}</b>
      </nav>

      <ProductFeature product={product} isActive />

      {related.length ? (
        <section className="catalog section-pad related-products">
          <Reveal className="section-head">
            <div>
              <p className="eyebrow">{pick(product.category?.name)}</p>
              <h2>{t('relatedProducts')}</h2>
            </div>
          </Reveal>
          <div className="catalog-grid">
            {related.map((entry) => <CatalogCard key={entry.id} product={entry} />)}
          </div>
        </section>
      ) : null}
    </main>
  );
}
